import React, { CSSProperties, memo, useState } from 'react';
import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { AlignVerticalCenter01, ChevronSelectorVertical } from '@untitledui-pro/icons/line';
import Presets from './Presets';
import Pipeline from './Pipeline';
import { useGlobalBucket } from '@gui/store/persistent/persistentStore';

type FiltersPresetsTabsProps = {
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    panelsExpanded: Record<string, boolean>;
    style?: CSSProperties;
};

const FiltersPresetsTabs: React.FC<FiltersPresetsTabsProps> = ({ isCollapsed = false, onToggleCollapse, panelsExpanded, style }) => {
    const [activeTab, setActiveTab] = useState<'filters' | 'presets'>('filters');
    const multimode = useGlobalBucket((s) => s.data.multimode);
    const isMixerMode = useGlobalBucket((s) => s.data.isMixerMode);
    const hideFiltersInMulti = multimode && isMixerMode;
    const expandedCount = Object.values(panelsExpanded).filter(Boolean).length;
    const disableToggle = !isCollapsed && expandedCount === 1;

    const handleCollapseToggle = () => {
        if (onToggleCollapse) onToggleCollapse();
    };

    return (
        <div
            style={style}
            className={`data-panel data-panel--filter ${isCollapsed ? 'data-panel--collapsed' : ''}`}
            data-guided-tour="guided-tour-filters-panel"
        >
            <div className="data-panel-tabs">
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, value) => {
                                if (isCollapsed) return;
                                const next = value as 'filters' | 'presets';
                                setActiveTab(next);
                            }}
                            sx={{
                                '& .MuiTabs-flexContainer': {
                                    gap: 4,
                                },
                            }}
                            aria-label="Filter panel tabs"
                        >
                            <Tab value="filters" label="FILTERS" disabled={isCollapsed} />
                            <Tab value="presets" label="PRESETS" disabled={isCollapsed} />
                        </Tabs>
                    </Box>
                    <IconButton
                        data-testid="filter-panel-collapse-toggle"
                        size="small"
                        onClick={handleCollapseToggle}
                        disabled={disableToggle}
                        sx={{
                            position: 'absolute',
                            right: -8,
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                boxShadow: 'none',
                                backgroundColor: 'transparent',
                            },
                            '&:active': {
                                boxShadow: 'none',
                            },
                            '&:focus-visible': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                transition: 'transform 120ms ease',
                                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            }}
                        >
                            {isCollapsed ? (
                                <ChevronSelectorVertical className="icon-size-small" />
                            ) : (
                                <AlignVerticalCenter01 className="icon-size-small" />
                            )}
                        </Box>
                    </IconButton>
                </Box>
            </div>

            {!isCollapsed && (
                <div className="data-panel-content">
                    <div className="data-panel-scroll">
                        <Box
                            sx={{
                                display: activeTab === 'filters' ? 'flex' : 'none',
                                height: '100%',
                                flexDirection: 'column',
                            }}
                        >
                            {hideFiltersInMulti ? (
                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        px: 2,
                                        color: 'text.secondary',
                                    }}
                                >
                                    <Typography variant="body2">
                                        Filters are unavailable in MultiMode. Switch back to Mixer mode to apply filters.
                                    </Typography>
                                </Box>
                            ) : (
                                <Pipeline />
                            )}
                        </Box>
                        <Box
                            sx={{
                                display: activeTab === 'presets' ? 'flex' : 'none',
                                height: '100%',
                                flexDirection: 'column',
                            }}
                        >
                            <Presets />
                        </Box>
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(FiltersPresetsTabs);
